/**
 * Cache management for AI predictions chart data
 * Handles caching of time series and scenario data for gold and silver predictions
 */

export interface ChartDataPoint {
  date: string;
  timestamp: number;
  historical?: number;
  predictedHigh?: number;
  predictedAverage?: number;
  predictedLow?: number;
  high?: number;
  low?: number;
}

export interface PredictionScenarios {
  high: { price: number; percentage: number; probability: number };
  average: { price: number; percentage: number; probability: number };
  low: { price: number; percentage: number; probability: number };
}

export interface MetalChartData {
  metal: 'ORO' | 'ARGENTO';
  currentPrice: number;
  targetPrice: number;
  targetPercentage: number;
  timeframe: string;
  confidence: number;
  timeSeries: ChartDataPoint[];
  scenarios: PredictionScenarios;
}

export interface ChartCacheData {
  gold: MetalChartData | null;
  silver: MetalChartData | null;
}

// In-memory cache for chart data
let chartCache: ChartCacheData = {
  gold: null,
  silver: null
};

// Cache timestamp for invalidation
let cacheTimestamp: number = 0;

// Cache duration: 1 hour
const CACHE_DURATION = 60 * 60 * 1000;

/**
 * Updates the chart cache with new data
 */
export const updateChartCache = (data: ChartCacheData): void => {
  console.log('[ChartCache] 💾 Updating chart cache with new data');
  
  chartCache = {
    gold: data.gold ? { ...data.gold } : null,
    silver: data.silver ? { ...data.silver } : null
  };
  
  cacheTimestamp = Date.now();
  
  console.log('[ChartCache] ✅ Chart cache updated successfully', {
    hasGold: !!chartCache.gold,
    hasSilver: !!chartCache.silver,
    timestamp: new Date(cacheTimestamp).toISOString()
  });
};

/**
 * Gets chart data from cache
 */
export const getChartCache = (): ChartCacheData | null => {
  const now = Date.now();
  const isExpired = (now - cacheTimestamp) > CACHE_DURATION;
  
  if (isExpired || cacheTimestamp === 0) {
    console.log('[ChartCache] ⏰ Chart cache expired or empty');
    return null;
  }
  
  console.log('[ChartCache] 📊 Returning cached chart data', {
    hasGold: !!chartCache.gold,
    hasSilver: !!chartCache.silver,
    age: Math.round((now - cacheTimestamp) / 1000 / 60) + ' minutes'
  });
  
  return {
    gold: chartCache.gold ? { ...chartCache.gold } : null,
    silver: chartCache.silver ? { ...chartCache.silver } : null
  };
};

/**
 * Clears the chart cache
 */
export const clearChartCache = (): void => {
  console.log('[ChartCache] 🗑️ Clearing chart cache');
  
  chartCache = {
    gold: null,
    silver: null
  };
  
  cacheTimestamp = 0;
};

/**
 * Checks if chart cache is valid and not expired
 */
export const isChartCacheValid = (): boolean => {
  const now = Date.now();
  const isExpired = (now - cacheTimestamp) > CACHE_DURATION;
  
  return !isExpired && cacheTimestamp > 0 && !!(chartCache.gold || chartCache.silver);
};

/**
 * Gets cache info for debugging
 */
export const getChartCacheInfo = () => {
  const now = Date.now();
  const age = cacheTimestamp > 0 ? now - cacheTimestamp : 0;
  
  return {
    hasData: !!(chartCache.gold || chartCache.silver),
    hasGold: !!chartCache.gold,
    hasSilver: !!chartCache.silver,
    timestamp: cacheTimestamp,
    age: age,
    ageMinutes: Math.round(age / 1000 / 60),
    isExpired: age > CACHE_DURATION,
    isValid: isChartCacheValid()
  };
};

/**
 * Alias for getChartCache to maintain compatibility with existing imports
 * @deprecated Use getChartCache instead
 */
export const getChartDataFromCache = getChartCache;
